import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSupportTickets, updateSupportTicket, getSupportStats, getAllUsers, replyToSupportTicket, getSupportTicket } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

function Support() {
  const { showSuccess, showError } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    urgent: 0,
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketDetailOpen, setIsTicketDetailOpen] = useState(false);
  const [updatingTicket, setUpdatingTicket] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
  });
  const [viewedTickets, setViewedTickets] = useState(new Set()); // Track which tickets have been viewed

  // Initial fetch with loading state
  const fetchTickets = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const token = localStorage.getItem('auth_token');
      if (!token) {
        if (showLoading) {
          showError('Authentication required. Please log in again.');
        }
        return;
      }
      const data = await getSupportTickets(token, true, statusFilter, priorityFilter);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch support tickets:', error);
      // Only show error on initial load, not on silent refreshes
      if (showLoading) {
        showError(error.message || 'Failed to load support tickets. Please refresh the page.');
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [statusFilter, priorityFilter, showError]);

  // Silent background refresh (no loading state)
  const refreshTicketsSilently = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await getSupportTickets(token, true, statusFilter, priorityFilter);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      // Silently fail on background refreshes - errors are logged but not shown
      console.error('Failed to refresh support tickets silently:', error);
    }
  }, [statusFilter, priorityFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await getSupportStats(token);
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch support stats:', error);
    }
  }, []);

  const fetchAdmins = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const data = await getAllUsers(token);
      // Filter to only admins/managers
      const adminUsers = data.filter(user => 
        ['super_admin', 'administrator', 'manager'].includes(user.role?.name)
      );
      setAdmins(adminUsers);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  }, []);

  // Fetch admins on mount only (they don't change frequently)
  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const ticketPollingIntervalRef = useRef(null);
  const ticketsPollingIntervalRef = useRef(null);

  // Real-time updates for tickets list and stats (poll every 3 seconds)
  useEffect(() => {
    // Initial fetch with loading state
    fetchTickets(true);
    fetchStats();
    
    // Set up polling for tickets list and stats (silent background refresh)
    ticketsPollingIntervalRef.current = setInterval(() => {
      refreshTicketsSilently();
      fetchStats();
    }, 3000); // Poll every 3 seconds

    return () => {
      if (ticketsPollingIntervalRef.current) {
        clearInterval(ticketsPollingIntervalRef.current);
        ticketsPollingIntervalRef.current = null;
      }
    };
  }, [statusFilter, priorityFilter, fetchTickets, fetchStats, refreshTicketsSilently]);

  // Check if ticket has unread customer reply (needs admin response)
  const hasUnreadCustomerReply = (ticket) => {
    // If ticket has been viewed, don't highlight
    if (viewedTickets.has(ticket.id)) return false;
    
    // If ticket has no messages, it's a new ticket (needs initial response)
    if (!ticket.messages || ticket.messages.length === 0) {
      // Check if it's a new ticket (created but no admin reply yet)
      return true;
    }
    
    // Get the last message
    const lastMessage = ticket.messages[ticket.messages.length - 1];
    
    // If last message is from customer (not admin), it needs a response
    return !lastMessage.is_admin;
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setUpdateData({
      status: ticket.status,
      priority: ticket.priority,
      assigned_to: ticket.assigned_to || '',
    });
    setReplyText('');
    setIsTicketDetailOpen(true);
    
    // Mark ticket as viewed (remove highlight)
    setViewedTickets(prev => new Set([...prev, ticket.id]));
    
    // Fetch full ticket details with messages
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const fullTicket = await getSupportTicket(ticket.id, token);
      setSelectedTicket(fullTicket);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
    }
  };

  // Real-time updates for ticket conversation when modal is open
  useEffect(() => {
    if (isTicketDetailOpen && selectedTicket?.id) {
      const ticketId = selectedTicket.id; // Capture ticket ID
      const pollTicketUpdates = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          if (!token) return;
          const updatedTicket = await getSupportTicket(ticketId, token);
          
          // Use functional update to avoid dependency on selectedTicket
          setSelectedTicket(prevTicket => {
            if (!prevTicket) return updatedTicket;
            
            // Only update if there are new messages or status/priority changed
            const currentLastMessageId = prevTicket.messages && prevTicket.messages.length > 0
              ? prevTicket.messages[prevTicket.messages.length - 1]?.id
              : null;
            const updatedLastMessageId = updatedTicket.messages && updatedTicket.messages.length > 0
              ? updatedTicket.messages[updatedTicket.messages.length - 1]?.id
              : null;
            
            // Update if messages changed or ticket status/priority changed
            if (currentLastMessageId !== updatedLastMessageId || 
                prevTicket.status !== updatedTicket.status ||
                prevTicket.priority !== updatedTicket.priority) {
              // Update updateData state as well
              setUpdateData({
                status: updatedTicket.status,
                priority: updatedTicket.priority,
                assigned_to: updatedTicket.assigned_to || '',
              });
              // Also refresh the ticket list and stats to keep them in sync (silent refresh)
              refreshTicketsSilently().catch(() => {});
              fetchStats().catch(() => {});
              return updatedTicket;
            }
            return prevTicket;
          });
        } catch (error) {
          console.error('Failed to poll ticket updates:', error);
        }
      };

      // Poll every 2 seconds for real-time updates
      ticketPollingIntervalRef.current = setInterval(pollTicketUpdates, 2000);

      return () => {
        if (ticketPollingIntervalRef.current) {
          clearInterval(ticketPollingIntervalRef.current);
          ticketPollingIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval when modal closes
      if (ticketPollingIntervalRef.current) {
        clearInterval(ticketPollingIntervalRef.current);
        ticketPollingIntervalRef.current = null;
      }
    }
  }, [isTicketDetailOpen, selectedTicket?.id, fetchTickets, fetchStats, refreshTicketsSilently]);

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;
    
    setUpdatingTicket(selectedTicket.id);
    try {
      const token = localStorage.getItem('auth_token');
      const updatePayload = {};
      
      if (updateData.status && updateData.status !== selectedTicket.status) {
        updatePayload.status = updateData.status;
      }
      
      if (updateData.priority && updateData.priority !== selectedTicket.priority) {
        updatePayload.priority = updateData.priority;
      }
      
      if (updateData.assigned_to !== selectedTicket.assigned_to) {
        updatePayload.assigned_to = updateData.assigned_to || null;
      }

      // Handle reply separately if provided
      if (replyText.trim()) {
        await replyToSupportTicket(selectedTicket.id, replyText.trim(), token, true);
        setReplyText('');
      }

      // Update ticket status/priority/assignment if changed
      if (Object.keys(updatePayload).length > 0) {
        await updateSupportTicket(selectedTicket.id, updatePayload, token);
      }

      if (replyText.trim() || Object.keys(updatePayload).length > 0) {
        showSuccess('Support ticket updated successfully.');
        
        // Mark ticket as viewed when replied to (remove highlight)
        if (replyText.trim()) {
          setViewedTickets(prev => new Set([...prev, selectedTicket.id]));
        }
        
        // Immediate refresh after update (silent refresh - polling will continue after)
        await refreshTicketsSilently();
        await fetchStats();
        // Trigger immediate notification refresh
        window.dispatchEvent(new Event('notification-refresh'));
        
        // Refresh the selected ticket to get updated messages
        const updatedTicket = await getSupportTicket(selectedTicket.id, token);
        setSelectedTicket(updatedTicket);
        // Trigger immediate notification refresh
        window.dispatchEvent(new Event('notification-refresh'));
        setUpdateData({
          status: updatedTicket.status,
          priority: updatedTicket.priority,
          assigned_to: updatedTicket.assigned_to || '',
        });
      } else {
        showError('Please make at least one change.');
      }
    } catch (error) {
      console.error('Failed to update support ticket:', error);
      showError(error.message || 'Failed to update support ticket.');
    } finally {
      setUpdatingTicket(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800 border-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      resolved: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800 border-gray-300',
      medium: 'bg-blue-100 text-blue-800 border-blue-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      urgent: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Support Tickets</h1>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Total</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4 border border-blue-200">
          <div className="text-sm text-blue-600">Open</div>
          <div className="text-2xl font-bold text-blue-900">{stats.open}</div>
        </div>
        <div className="bg-yellow-50 rounded-lg shadow p-4 border border-yellow-200">
          <div className="text-sm text-yellow-600">In Progress</div>
          <div className="text-2xl font-bold text-yellow-900">{stats.in_progress}</div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4 border border-green-200">
          <div className="text-sm text-green-600">Resolved</div>
          <div className="text-2xl font-bold text-green-900">{stats.resolved}</div>
        </div>
        <div className="bg-gray-50 rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Closed</div>
          <div className="text-2xl font-bold text-gray-900">{stats.closed}</div>
        </div>
        <div className="bg-red-50 rounded-lg shadow p-4 border border-red-200">
          <div className="text-sm text-red-600">Urgent</div>
          <div className="text-2xl font-bold text-red-900">{stats.urgent}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading tickets...</p>
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center border border-gray-200">
          <p className="text-gray-600">No support tickets found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tickets.map((ticket) => {
                  const needsResponse = hasUnreadCustomerReply(ticket);
                  return (
                  <tr 
                    key={ticket.id} 
                    className={`hover:bg-gray-50 transition-colors ${
                      needsResponse 
                        ? 'bg-amber-50 hover:bg-amber-100 border-l-4 border-amber-500' 
                        : ''
                    }`}
                  >
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">#{ticket.id}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{ticket.user?.name || ticket.user?.email || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-900 max-w-xs truncate">{ticket.subject}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ticket.assigned_admin?.name || 'Unassigned'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(ticket.created_at)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className="text-primary hover:text-primary-dark font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {isTicketDetailOpen && selectedTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex items-start justify-center min-h-full px-2 py-2 sm:px-4 sm:py-8 sm:min-h-screen">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-60 backdrop-blur-sm" onClick={() => setIsTicketDetailOpen(false)}></div>

            <div className="relative bg-white rounded-xl sm:rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl my-2 sm:my-8 max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-white">Ticket #{selectedTicket.id}</h3>
                      <span className={`px-2.5 py-1 text-[10px] sm:text-xs font-semibold rounded-full border-2 ${getStatusColor(selectedTicket.status)} bg-white/90 backdrop-blur-sm`}>
                        {selectedTicket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`px-2.5 py-1 text-[10px] sm:text-xs font-semibold rounded-full border-2 ${getPriorityColor(selectedTicket.priority)} bg-white/90 backdrop-blur-sm`}>
                        {selectedTicket.priority.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-white/90 font-medium truncate">{selectedTicket.subject}</p>
                  </div>
                  <button
                    onClick={() => setIsTicketDetailOpen(false)}
                    className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-all flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="space-y-4 sm:space-y-6">
                  {/* User Info Card */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-3 sm:p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white text-sm sm:text-base font-bold flex-shrink-0">
                        {selectedTicket.user?.name ? selectedTicket.user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">{selectedTicket.user?.name || selectedTicket.user?.email || 'N/A'}</p>
                        <p className="text-xs sm:text-sm text-gray-500">Customer</p>
                      </div>
                    </div>
                  </div>

                  {/* Initial Message */}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">Initial Message</label>
                    <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-lg p-3 sm:p-4 text-sm sm:text-base text-gray-900 whitespace-pre-wrap border-2 border-gray-200 break-words max-h-48 overflow-y-auto shadow-sm">
                      {selectedTicket.message}
                    </div>
                  </div>

                  {/* Conversation Thread */}
                  {selectedTicket.messages && selectedTicket.messages.length > 0 && (
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Conversation Thread</label>
                      <div className="space-y-4 max-h-[35vh] sm:max-h-64 md:max-h-80 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 sm:p-4 bg-gradient-to-br from-gray-50/50 via-white to-gray-50/50">
                        {selectedTicket.messages.map((msg) => (
                          <div key={msg.id} className={`flex flex-col ${msg.is_admin ? 'items-end' : 'items-start'}`}>
                            <div className={`flex items-center gap-2 sm:gap-3 mb-2 ${msg.is_admin ? 'flex-row-reverse' : ''}`}>
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0 shadow-md ${
                                msg.is_admin 
                                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 ring-2 ring-blue-200' 
                                  : 'bg-gradient-to-br from-primary to-primary-dark ring-2 ring-primary/20'
                              }`}>
                                {msg.user?.name ? msg.user.name.charAt(0).toUpperCase() : (msg.is_admin ? 'A' : 'U')}
                              </div>
                              <div className={`min-w-0 flex-1 ${msg.is_admin ? 'text-right' : ''}`}>
                                <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                  {msg.is_admin ? (msg.user?.name || 'Admin') : (msg.user?.name || 'Customer')}
                                  {msg.is_admin && <span className="ml-1.5 px-2 py-0.5 text-[10px] text-blue-600 bg-blue-100 rounded-full font-medium">Admin</span>}
                                </p>
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{formatDate(msg.created_at)}</p>
                              </div>
                            </div>
                            <div className={`max-w-[85%] sm:max-w-[80%] rounded-xl p-3 sm:p-4 text-sm sm:text-base whitespace-pre-wrap border-2 break-words shadow-sm ${
                              msg.is_admin
                                ? 'bg-gradient-to-br from-blue-50 via-blue-50/80 to-indigo-50 border-blue-300 text-gray-900'
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}>
                              {msg.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ticket Management Section */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 p-4 sm:p-5">
                    <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-4 uppercase tracking-wide">Ticket Management</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Status</label>
                        <select
                          value={updateData.status}
                          onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white font-medium"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved (Auto-Closes)</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Priority</label>
                        <select
                          value={updateData.priority}
                          onChange={(e) => setUpdateData({ ...updateData, priority: e.target.value })}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white font-medium"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">Assign To</label>
                      <select
                        value={updateData.assigned_to}
                        onChange={(e) => setUpdateData({ ...updateData, assigned_to: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white font-medium"
                      >
                        <option value="">Unassigned</option>
                        {admins.map((admin) => (
                          <option key={admin.id} value={admin.id}>
                            {admin.name} ({admin.role?.display_name})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Add Reply Section */}
                  <div className="bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/30 rounded-xl border-2 border-blue-200 p-4 sm:p-5">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Add Reply</label>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none bg-white shadow-sm"
                      placeholder="Type your reply here..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2 border-t-2 border-gray-200">
                    <button
                      onClick={() => setIsTicketDetailOpen(false)}
                      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdateTicket}
                      disabled={updatingTicket === selectedTicket.id}
                      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg hover:shadow-lg hover:shadow-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    >
                      {updatingTicket === selectedTicket.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Updating...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Update Ticket
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Support;
